import React, { useEffect, useState } from "react";
import "./StockDataAnalysis.css";
import { environment } from "environment/environment";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";

export default function StockDataAnalysis() {
    // WebSocket connection
    const { stockAnalysisData } = useStockApiWebsocket(environment.stockApiDayRangeDataWSUrl);
    const [stockData, setStockData] = useState([]);

    /* ================= SORT STATE ================= */
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "asc"
    });

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
    };
    /* ============================================== */

    const getIndexTopBottomDataClass = (spotPrice, value) => {
        const diff = Math.abs(spotPrice - value);
        return diff <= 2.5 ? "bg-orange" : "";
    };

    useEffect(() => {
        if (stockAnalysisData && Array.isArray(stockAnalysisData) && stockAnalysisData.length > 0) {
            setStockData(stockAnalysisData);
            console.log("Stock Data Analysis WebSocket Data:", stockAnalysisData);
        }
    }, [stockAnalysisData]);

    const getBullishAndBearishClass = (value) => {
        if (value == null) return "";
        return value >= 0 ? "bullish-color" : "bearish-color";
    };

    const getPdcPdhPdlClass = (column, pdh, pdc, pdl) => {
        if (pdc == null) return "";

        const pdcNum = Number(pdc);
        const pdhNum = Number(pdh);
        const pdlNum = Number(pdl);

        if (
            pdh != null &&
            Math.abs(pdhNum - pdcNum) <= 3 &&
            ["PDH", "HIGH", "PDC", "CLOSE"].includes(column)
        ) {
            return "bg-yellow";
        }

        if (
            pdl != null &&
            Math.abs(pdlNum - pdcNum) <= 3 &&
            ["PDL", "LOW", "PDC", "CLOSE"].includes(column)
        ) {
            return "bg-yellow";
        }

        return "";
    };

    const getPdhPdlPdcDiff = (value1, value2) => {
        if (value1 == null || value2 == null || isNaN(value1) || isNaN(value2)) {
            return 0;
        }
        return Math.abs(value1 - value2);
    };

    /* ================= SORTED DATA ================= */
    const sortedStockData = React.useMemo(() => {
        if (!sortConfig.key) return stockAnalysisData;

        return [...stockAnalysisData].sort((a, b) => {
            let aVal = 0;
            let bVal = 0;

            switch (sortConfig.key) {
                case "PDH_PDC":
                    aVal = getPdhPdlPdcDiff(a.previousDayHigh, a.prev_close_price);
                    bVal = getPdhPdlPdcDiff(b.previousDayHigh, b.prev_close_price);
                    break;

                case "PDL_PDC":
                    aVal = getPdhPdlPdcDiff(a.previousDayLow, a.prev_close_price);
                    bVal = getPdhPdlPdcDiff(b.previousDayLow, b.prev_close_price);
                    break;

                case "DH_CLOSE":
                    aVal = getPdhPdlPdcDiff(a.high_price, a.ltp);
                    bVal = getPdhPdlPdcDiff(b.high_price, b.ltp);
                    break;

                case "DL_CLOSE":
                    aVal = getPdhPdlPdcDiff(a.low_price, a.ltp);
                    bVal = getPdhPdlPdcDiff(b.low_price, b.ltp);
                    break;

                default:
                    return 0;
            }

            return sortConfig.direction === "asc"
                ? aVal - bVal
                : bVal - aVal;
        });
    }, [stockAnalysisData, sortConfig]);
    /* =============================================== */

    // Header Data
    const headerSource = stockData?.[0] ?? {};
    const headerData = [
        {
            label1: "Date",
            value1: headerSource?.localDate ?? "--",
            label2: "Time",
            value2: headerSource?.localTime ?? "--",
        },
    ];

    return (
        <div className="stock-analysis-page">
            <header className="header">
                <nav className="nav-container">
                    <div className="left-nav header-flex">
                        <div className="logo-container">
                            <img
                                src="/assets/images/noptionwave-logo-1.png"
                                className="header-logo"
                                alt="noptionwave logo"
                            />
                        </div>

                        {headerData.map((item, idx) => (
                            <div className="header-item" key={idx}>
                                <span className="header-line">{item.label1} : {item.value1}</span>
                                <span className="header-line">{item.label2} : {item.value2}</span>
                            </div>
                        ))}
                    </div>
                </nav>
            </header>

            <div className="table-container">
                <table className="responsive-table table-bordered text-center sticky-header stock-day-range-table">
                    <thead>
                        <tr>
                            <th>SYMBOL</th>
                            <th>PDH</th>
                            <th>PDL</th>
                            <th>PDC</th>

                            <th className="sortable" onClick={() => handleSort("PDH_PDC")}>
                                PDH-PDC {sortConfig.key === "PDH_PDC" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                            </th>

                            <th className="sortable" onClick={() => handleSort("PDL_PDC")}>
                                PDL-PDC {sortConfig.key === "PDL_PDC" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                            </th>

                            <th>OPEN</th>
                            <th>DH</th>
                            <th>DL</th>
                            <th>CLOSE</th>

                            <th className="sortable" onClick={() => handleSort("DH_CLOSE")}>
                                DH-CLOSE {sortConfig.key === "DH_CLOSE" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                            </th>

                            <th className="sortable" onClick={() => handleSort("DL_CLOSE")}>
                                DL-CLOSE {sortConfig.key === "DL_CLOSE" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                            </th>

                            <th>LTP</th>
                            <th>CHG PTS</th>
                            <th>CHG (%)</th>
                        </tr>
                    </thead>

                    <tbody>
                        {sortedStockData?.length > 0 ? (
                            sortedStockData.map((row, idx) => (
                                <tr key={idx}>
                                    <td>{row.symbol?.split(":")[1]?.split("-")[0] ?? "--"}</td>
                                    <td className={getPdcPdhPdlClass("PDH", row.previousDayHigh, row.prev_close_price, row.previousDayLow)}>{row.previousDayHigh ?? "--"}</td>
                                    <td className={getPdcPdhPdlClass("PDL", row.previousDayHigh, row.prev_close_price, row.previousDayLow)}>{row.previousDayLow ?? "--"}</td>
                                    <td className={getPdcPdhPdlClass("PDC", row.previousDayHigh, row.prev_close_price, row.previousDayLow)}>{row.prev_close_price ?? "--"}</td>

                                    <td>{getPdhPdlPdcDiff(row.previousDayHigh, row.prev_close_price).toFixed(2)}</td>
                                    <td>{getPdhPdlPdcDiff(row.previousDayLow, row.prev_close_price).toFixed(2)}</td>

                                    <td>{row.open_price ?? "--"}</td>
                                    <td className={getPdcPdhPdlClass("HIGH", row.high_price, row.ltp, row.low_price)}>{row.high_price ?? "--"}</td>
                                    <td className={getPdcPdhPdlClass("LOW", row.high_price, row.ltp, row.low_price)}>{row.low_price ?? "--"}</td>
                                    <td className={getPdcPdhPdlClass("CLOSE", row.high_price, row.ltp, row.low_price)}>{row.ltp ?? "--"}</td>

                                    <td>{getPdhPdlPdcDiff(row.high_price, row.ltp).toFixed(2)}</td>
                                    <td>{getPdhPdlPdcDiff(row.low_price, row.ltp).toFixed(2)}</td>

                                    <td>{row.ltp ?? "--"}</td>
                                    <td className={getBullishAndBearishClass(row.ch)}>{row.ch ?? "--"}</td>
                                    <td className={getBullishAndBearishClass(row.chp)}>{row.chp ?? "--"}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="17">Waiting for live data...</td>
                            </tr>
                        )}
                    </tbody>

                    <tfoot className="fixed-footer">
                        <tr className="footer-left">
                            <td colSpan="15">noptionwave © 2026</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}
